import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Bell,
  CheckCheck,
  ChevronLeft,
  ChevronRight,
  Loader2,
  RefreshCcw,
  Trash2,
  X,
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { cn } from "@/lib/utils";
import { formatNumber } from "@/lib/formatters";
import {
  bulkDeleteNotifications,
  bulkMarkRead,
  deleteNotification,
  listNotifications,
  markAllRead,
  markRead,
} from "@/api/notifications";
import { useNotifications } from "@/hooks/useNotifications";
import {
  NotificationItem,
  NOTIFICATION_CATEGORIES,
  destinationFor,
} from "@/components/notifications/NotificationItem";
import {
  NotificationFilters,
  type NotificationFilter,
} from "@/components/notifications/NotificationFilters";
import type { NotificationRead } from "@/api/types";

const PAGE_SIZE = 20;
const FETCH_WINDOW = 100;

type LoadStatus = "loading" | "ok" | "error";

export default function NotificationsPage() {
  const navigate = useNavigate();
  const { unreadCount, setUnreadCount, refetch: refetchCount } =
    useNotifications();
  const [filter, setFilter] = useState<NotificationFilter>({ kind: "all" });
  const [items, setItems] = useState<NotificationRead[]>([]);
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState<LoadStatus>("loading");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);

  // Reset page + selection on filter change.
  useEffect(() => {
    setPage(1);
    setSelected(new Set());
  }, [filter]);

  const load = useCallback(async () => {
    setStatus("loading");
    setErrorMessage(null);
    try {
      const params: Parameters<typeof listNotifications>[0] = {
        limit: FETCH_WINDOW,
      };
      if (filter.kind === "unread") params.is_read = false;
      const res = await listNotifications(params);
      setItems(res.items);
      setStatus("ok");
    } catch (err) {
      setErrorMessage(extractMessage(err));
      setStatus("error");
    }
  }, [filter]);

  useEffect(() => {
    void load();
  }, [load]);

  // Category filtering happens client-side over the fetched window — the
  // backend's `type` param matches a single string and we want to surface
  // a category that groups several types.
  const filtered = useMemo(() => {
    if (filter.kind !== "category") return items;
    const allowed = new Set<string>(
      NOTIFICATION_CATEGORIES[filter.category] as readonly string[],
    );
    return items.filter((n) => allowed.has(n.type));
  }, [items, filter]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const visible = filtered.slice(
    (safePage - 1) * PAGE_SIZE,
    safePage * PAGE_SIZE,
  );
  const rangeStart = filtered.length === 0 ? 0 : (safePage - 1) * PAGE_SIZE + 1;
  const rangeEnd = Math.min(safePage * PAGE_SIZE, filtered.length);

  const visibleIds = useMemo(() => visible.map((n) => n.id), [visible]);
  const allVisibleSelected =
    visibleIds.length > 0 && visibleIds.every((id) => selected.has(id));
  const someVisibleSelected =
    !allVisibleSelected && visibleIds.some((id) => selected.has(id));

  function toggleAllVisible() {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allVisibleSelected) visibleIds.forEach((id) => next.delete(id));
      else visibleIds.forEach((id) => next.add(id));
      return next;
    });
  }

  function toggleOne(id: number, on: boolean) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (on) next.add(id);
      else next.delete(id);
      return next;
    });
  }

  async function handleClickItem(n: NotificationRead) {
    if (!n.is_read) {
      setItems((prev) =>
        prev.map((x) => (x.id === n.id ? { ...x, is_read: true } : x)),
      );
      setUnreadCount((c) => Math.max(0, c - 1));
      void markRead(n.id).catch(() => {
        void refetchCount();
      });
    }
    const dest = destinationFor(n);
    if (dest) navigate(dest);
  }

  async function handleToggleRead(n: NotificationRead) {
    const newRead = !n.is_read;
    setItems((prev) =>
      prev.map((x) => (x.id === n.id ? { ...x, is_read: newRead } : x)),
    );
    setUnreadCount((c) => Math.max(0, newRead ? c - 1 : c + 1));
    try {
      if (newRead) await markRead(n.id);
      else await bulkMarkRead([n.id], false);
    } catch {
      void load();
      void refetchCount();
    }
  }

  async function handleDeleteOne(n: NotificationRead) {
    const previous = items;
    setItems((prev) => prev.filter((x) => x.id !== n.id));
    if (!n.is_read) setUnreadCount((c) => Math.max(0, c - 1));
    try {
      await deleteNotification(n.id);
    } catch (err) {
      setItems(previous);
      void refetchCount();
      toast.error(extractMessage(err));
    }
  }

  async function handleMarkAllRead() {
    if (unreadCount === 0) return;
    const previous = items;
    setItems((prev) => prev.map((n) => ({ ...n, is_read: true })));
    setUnreadCount(0);
    try {
      await markAllRead();
      toast.success("All notifications marked as read");
    } catch (err) {
      setItems(previous);
      void refetchCount();
      toast.error(extractMessage(err));
    }
  }

  async function handleBulkMarkRead(isRead: boolean) {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    setBulkBusy(true);
    try {
      await bulkMarkRead(ids, isRead);
      setItems((prev) =>
        prev.map((n) =>
          selected.has(n.id) ? { ...n, is_read: isRead } : n,
        ),
      );
      void refetchCount();
      setSelected(new Set());
      toast.success(
        isRead
          ? `${ids.length} marked as read`
          : `${ids.length} marked as unread`,
      );
    } catch (err) {
      toast.error(extractMessage(err));
    } finally {
      setBulkBusy(false);
    }
  }

  async function handleBulkDelete() {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    setBulkBusy(true);
    try {
      await bulkDeleteNotifications(ids);
      setItems((prev) => prev.filter((n) => !selected.has(n.id)));
      void refetchCount();
      setSelected(new Set());
      toast.success(`${ids.length} deleted`);
    } catch (err) {
      toast.error(extractMessage(err));
    } finally {
      setBulkBusy(false);
    }
  }

  return (
    <div className="space-y-5 pb-24">
      <header className="flex items-end justify-between gap-3">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-semibold tracking-tight text-slate-900">
              Notifications
            </h1>
            {unreadCount > 0 && (
              <span className="rounded-full bg-indigo-100 px-2.5 py-0.5 text-xs font-medium text-indigo-700">
                {unreadCount} unread
              </span>
            )}
          </div>
          <p className="text-sm text-slate-500">
            All updates across your projects.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={load}
            disabled={status === "loading"}
          >
            <RefreshCcw
              className={
                status === "loading"
                  ? "mr-2 h-3.5 w-3.5 animate-spin"
                  : "mr-2 h-3.5 w-3.5"
              }
            />
            Refresh
          </Button>
          {unreadCount > 0 && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => void handleMarkAllRead()}
              className="border-indigo-200 text-indigo-700 hover:bg-indigo-50"
            >
              <CheckCheck className="mr-2 h-3.5 w-3.5" />
              Mark all as read
            </Button>
          )}
        </div>
      </header>

      <NotificationFilters value={filter} onChange={setFilter} />

      {status === "error" && (
        <ErrorBanner message={errorMessage} onRetry={load} />
      )}

      <div className="rounded-plane bg-white shadow-plane">
        <div className="flex items-center justify-between border-b border-slate-900/[0.06] px-6 py-3 text-xs">
          <div className="flex items-center gap-3">
            {visibleIds.length > 0 && (
              <Checkbox
                checked={
                  allVisibleSelected
                    ? true
                    : someVisibleSelected
                      ? "indeterminate"
                      : false
                }
                onCheckedChange={toggleAllVisible}
                aria-label="Select all visible"
              />
            )}
            <span className="font-medium text-slate-500">
              {filtered.length === 0
                ? "0 notifications"
                : `Showing ${rangeStart}–${rangeEnd} of ${formatNumber(filtered.length)}`}
            </span>
          </div>
        </div>

        {status === "loading" && items.length === 0 ? (
          <div className="flex items-center justify-center px-6 py-16 text-sm text-slate-400">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Loading…
          </div>
        ) : visible.length === 0 ? (
          <EmptyState filter={filter} onSeeAll={() => setFilter({ kind: "all" })} />
        ) : (
          <ul>
            <AnimatePresence initial={false} mode="popLayout">
              {visible.map((n) => (
                <NotificationItem
                  key={n.id}
                  notification={n}
                  onClick={() => void handleClickItem(n)}
                  selected={selected.has(n.id)}
                  onSelectChange={(v) => toggleOne(n.id, v)}
                  onToggleRead={() => void handleToggleRead(n)}
                  onDelete={() => void handleDeleteOne(n)}
                />
              ))}
            </AnimatePresence>
          </ul>
        )}

        {filtered.length > PAGE_SIZE && (
          <div className="flex items-center justify-end gap-2 border-t border-slate-900/[0.06] px-6 py-3 text-xs text-slate-500">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={safePage <= 1}
            >
              <ChevronLeft className="h-3.5 w-3.5" />
            </Button>
            <span className="px-2">
              {safePage} / {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={safePage >= totalPages}
            >
              <ChevronRight className="h-3.5 w-3.5" />
            </Button>
          </div>
        )}
      </div>

      {selected.size > 0 && (
        <BulkBar
          count={selected.size}
          busy={bulkBusy}
          onMarkRead={() => void handleBulkMarkRead(true)}
          onMarkUnread={() => void handleBulkMarkRead(false)}
          onDelete={() => void handleBulkDelete()}
          onClear={() => setSelected(new Set())}
        />
      )}
    </div>
  );
}

interface BulkBarProps {
  count: number;
  busy: boolean;
  onMarkRead: () => void;
  onMarkUnread: () => void;
  onDelete: () => void;
  onClear: () => void;
}

function BulkBar({
  count,
  busy,
  onMarkRead,
  onMarkUnread,
  onDelete,
  onClear,
}: BulkBarProps) {
  return (
    <div className="fixed inset-x-0 bottom-6 z-30 flex justify-center px-4">
      <div className="flex items-center gap-3 rounded-plane bg-white px-4 py-2.5 shadow-elevated">
        <span className="text-sm font-medium text-slate-700">
          {count} selected
        </span>
        <span className="h-5 w-px bg-slate-200" aria-hidden />
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={onMarkRead}
          disabled={busy}
        >
          <CheckCheck className="mr-2 h-3.5 w-3.5" />
          Mark as read
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={onMarkUnread}
          disabled={busy}
        >
          Mark as unread
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={onDelete}
          disabled={busy}
          className="border-rose-200 text-rose-700 hover:bg-rose-50"
        >
          <Trash2 className="mr-2 h-3.5 w-3.5" />
          Delete
        </Button>
        <button
          type="button"
          onClick={onClear}
          aria-label="Clear selection"
          className="rounded-md p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}

function EmptyState({
  filter,
  onSeeAll,
}: {
  filter: NotificationFilter;
  onSeeAll: () => void;
}) {
  if (filter.kind === "all") {
    return (
      <div className="flex flex-col items-center px-6 py-16 text-center">
        <Bell className="h-12 w-12 text-slate-300" />
        <p className="mt-3 text-base font-medium text-slate-700">
          No notifications yet
        </p>
        <p className="mt-1 text-sm text-slate-500">
          You&rsquo;ll be notified when something important happens.
        </p>
      </div>
    );
  }
  const noun =
    filter.kind === "unread"
      ? "unread"
      : filter.category === "leads"
        ? "lead"
        : filter.category === "engine"
          ? "engine"
          : filter.category === "tasks"
            ? "task"
            : "team";
  return (
    <div className="flex flex-col items-center px-6 py-16 text-center">
      <Bell className="h-10 w-10 text-slate-300" />
      <p className="mt-3 text-base font-medium text-slate-700">
        No {noun} notifications
      </p>
      <button
        type="button"
        onClick={onSeeAll}
        className={cn(
          "mt-3 text-sm font-medium text-indigo-600 hover:text-indigo-500",
        )}
      >
        View all
      </button>
    </div>
  );
}

function ErrorBanner({
  message,
  onRetry,
}: {
  message: string | null;
  onRetry: () => void;
}) {
  return (
    <div className="flex items-center justify-between rounded-xl border border-rose-200 bg-rose-50 px-4 py-3">
      <div>
        <p className="text-sm font-medium text-rose-700">
          Couldn&rsquo;t load notifications
        </p>
        {message && <p className="text-xs text-rose-600">{message}</p>}
      </div>
      <Button
        variant="outline"
        size="sm"
        onClick={onRetry}
        className="border-rose-200 bg-white text-rose-700 hover:bg-rose-100"
      >
        <RefreshCcw className="mr-2 h-3.5 w-3.5" />
        Retry
      </Button>
    </div>
  );
}

function extractMessage(err: unknown): string {
  if (axios.isAxiosError(err)) {
    if (err.code === "ERR_NETWORK") return "Cannot reach the server.";
    const detail = (err.response?.data as { detail?: unknown } | undefined)
      ?.detail;
    if (typeof detail === "string") return detail;
  }
  return "Something went wrong.";
}
